import { Bar, Doughnut } from "react-chartjs-2";
import { formatINR, PERIOD_KEYS, PERIOD_LABELS } from "../utils/formatters";

export default function StatsTab({
  statsPeriod,
  setStatsPeriod,
  statsData,
  isLoadingStats,
  pd,
}) {
  const completedTrips = pd.total_trips - pd.total_cancels;
  const completionRate = pd.total_trips
    ? Math.round((completedTrips / pd.total_trips) * 100)
    : 0;

  const barData = {
    labels: pd.chart_labels,
    datasets: [
      {
        data: pd.chart_trips,
        backgroundColor: "#2a78d6",
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.parsed.y} trips`,
        },
      },
    },
    scales: {
      x: {
        reverse: true,
        grid: { display: false },
        ticks: { color: "#898781", font: { size: 10 }, maxRotation: 0 },
      },
      y: {
        grid: { color: "#e1e0d9" },
        ticks: {
          color: "#898781",
          font: { size: 10 },
          maxTicksLimit: 6,
          precision: 0,
        },
        border: { display: false },
        beginAtZero: true,
      },
    },
  };

  const earningsBarData = {
    labels: pd.chart_labels,
    datasets: [
      {
        data: pd.chart_earnings,
        backgroundColor: "#1D9E75",
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
  };

  const earningsBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ₹${ctx.parsed.y.toLocaleString("en-IN")}`,
        },
      },
    },
    scales: {
      x: {
        reverse: true,
        grid: { display: false },
        ticks: { color: "#898781", font: { size: 10 }, maxRotation: 0 },
      },
      y: {
        grid: { color: "#e1e0d9" },
        ticks: {
          color: "#898781",
          font: { size: 10 },
          maxTicksLimit: 6,
          callback: (v) => (v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`),
        },
        border: { display: false },
        beginAtZero: true,
      },
    },
  };

  const donutData = {
    datasets: [
      {
        data: [completedTrips, pd.total_cancels],
        backgroundColor: ["#2a78d6", "#e1e0d9"],
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "72%",
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.parsed} trips`,
        },
      },
    },
  };

  return (
    <div role="tabpanel">
      <div className="p-period-bar">
        {PERIOD_KEYS.map((key) => (
          <button
            key={key}
            className={`p-period-btn ${statsPeriod === key ? "p-period-btn--active" : ""}`}
            onClick={() => setStatsPeriod(key)}
          >
            {PERIOD_LABELS[key]}
            {statsData[key] && <span className="p-period-dot">●</span>}
          </button>
        ))}
      </div>

      {isLoadingStats ? (
        <div className="p-stats-loading">
          <div className="skeleton" style={{ height: 80, marginBottom: 16 }} />
          <div className="skeleton" style={{ height: 200 }} />
        </div>
      ) : (
        <>
          <div className="p-metric-grid">
            <div className="p-metric p-metric--blue">
              <p className="p-metric-val">{pd.total_trips}</p>
              <p className="p-metric-label">Trips</p>
            </div>
            <div className="p-metric">
              <p className="p-metric-val">
                {pd.avg_rating?.toFixed(1) ?? "—"}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="#EFB022"
                  stroke="#EFB022"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  style={{
                    marginLeft: 4,
                    verticalAlign: "middle",
                    marginBottom: 2,
                  }}
                >
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </p>
              <p className="p-metric-label">Rating</p>
            </div>
            <div className="p-metric p-metric--green">
              <p className="p-metric-val">{formatINR(pd.total_earnings)}</p>
              <p className="p-metric-label">Earnings</p>
            </div>
            <div className="p-metric">
              <p className="p-metric-val">
                {pd.total_work_hours.toLocaleString("en-IN")}
              </p>
              <p className="p-metric-label">Hrs online</p>
            </div>
          </div>

          {pd.chart_labels.length > 0 && (
            <>
              <p className="p-chart-label">
                Trips — {PERIOD_LABELS[statsPeriod]}
              </p>
              <div className="p-chart-wrap">
                <Bar data={barData} options={barOptions} />
              </div>

              <p className="p-chart-label" style={{ marginTop: 20 }}>
                Earnings — {PERIOD_LABELS[statsPeriod]}
              </p>
              <div className="p-chart-wrap">
                <Bar data={earningsBarData} options={earningsBarOptions} />
              </div>
            </>
          )}

          <p className="p-chart-label" style={{ marginTop: 20 }}>
            Completion rate
          </p>
          <div className="p-donut-row">
            <div className="p-donut-wrap">
              <Doughnut data={donutData} options={donutOptions} />
              <div className="p-donut-center" aria-hidden="true">
                <span className="p-donut-pct">{completionRate}%</span>
                <span className="p-donut-sub">Done</span>
              </div>
            </div>
            <div className="p-donut-legend">
              <div className="p-legend-item">
                <span className="p-legend-dot p-legend-dot--blue" />
                Completed — {completedTrips}
              </div>
              <div className="p-legend-item">
                <span className="p-legend-dot p-legend-dot--gray" />
                Cancelled — {pd.total_cancels}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}